import * as React from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import DiscountIcon from '@mui/icons-material/Discount';
import LoyaltyOutlinedIcon from '@mui/icons-material/LoyaltyOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import AuthContext from '../../context/AuthContext';
import useAxios from '../../utils/useAxios';
import { APPBAR_HEIGHT, SIDEBAR_WIDTH, TRUE_COLORS, TRUE_LOGO_WHITE } from '../../theme';
import { businessSlug, setBusinessSlug, withoutBusiness } from '../../utils/businessHistory';
import { Features, ModuleKey, moduleOn } from '../../utils/features';

/** `modules`: shown when the business uses any of them (utils/features). `admin`: only for administrators (who change
 * products, stock, promotions, the team…; the server refuses anyone else's change). */
type NavItem = { label: string; to: string; icon: React.ReactElement; modules?: ModuleKey[]; admin?: boolean };

// Same sections, item styling and colors as the Desktop POS sidebar.
const NAV_SECTIONS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Principal',
    items: [
      { label: 'Inicio', to: '/admin', icon: <HomeIcon /> },
      { label: 'Pedidos', to: '/admin/orders', icon: <ShoppingBagOutlinedIcon />, modules: ['orders', 'encargos'] },
      { label: 'Análisis', to: '/admin/analysis', icon: <QueryStatsIcon /> },
      { label: 'Clientes', to: '/admin/clients', icon: <PeopleAltIcon /> },
      { label: 'Productos', to: '/admin/products', icon: <DashboardIcon />, admin: true },
      { label: 'Inventario', to: '/admin/inventory', icon: <InventoryIcon />, modules: ['inventory'], admin: true },
      { label: 'Promociones', to: '/admin/promotions', icon: <DiscountIcon />, modules: ['promotions'], admin: true },
      { label: 'Lealtad', to: '/admin/loyalty', icon: <LoyaltyOutlinedIcon />, modules: ['loyalty'], admin: true },
      { label: 'Saldo', to: '/admin/balance', icon: <AccountBalanceWalletOutlinedIcon />, modules: ['balance'], admin: true },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { label: 'Organización', to: '/admin/organization', icon: <BusinessIcon />, admin: true },
      { label: 'Marca y catálogo', to: '/admin/brand', icon: <PaletteOutlinedIcon />, admin: true },
      { label: 'Actividad', to: '/admin/activity', icon: <HistoryIcon />, admin: true },
      { label: 'Configuración', to: '/admin/settings', icon: <SettingsIcon /> },
    ],
  },
];

// Detail and child routes highlight their parent section.
const SECTION_PREFIXES: Record<string, string[]> = {
  '/admin': ['/admin/daily-report'],
  '/admin/products': ['/admin/add-product', '/admin/categories', '/admin/catalog', '/admin/modifiers', '/admin/common-products'],
  '/admin/promotions': ['/admin/campaigns'],
  '/admin/organization': ['/admin/locations', '/admin/members'],
  '/admin/settings': ['/admin/profile'],
};

const isActive = (to: string, fullPath: string) => {
  const pathname = withoutBusiness(fullPath);  // /cafebolero/admin/clients is the /admin/clients section
  if (to === '/admin') return pathname === '/admin' || pathname === '/admin/' || SECTION_PREFIXES['/admin'].some(p => pathname.startsWith(p));
  return pathname.startsWith(to) || (SECTION_PREFIXES[to] ?? []).some(p => pathname.startsWith(p));
};

const navItemSx = {
  borderRadius: '10px',
  mx: 0.5,
  mb: 0.5,
  color: 'rgba(255,255,255,0.65)',
  '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.45)', minWidth: 40 },
  '& .MuiListItemText-primary': { fontWeight: 500, fontSize: '0.875rem' },
  '&:hover': {
    bgcolor: 'rgba(255,255,255,0.07)',
    color: '#fff',
    '& .MuiListItemIcon-root': { color: TRUE_COLORS.secondary },
  },
  '&.Mui-selected': {
    bgcolor: 'rgba(111,137,148,0.22)',
    color: '#fff',
    '& .MuiListItemIcon-root': { color: TRUE_COLORS.secondary },
    '&:hover': { bgcolor: 'rgba(111,137,148,0.30)' },
  },
};

// The logo SVGs are 500×500 squares with the wordmark in a horizontal band; crop to that band.
const logoSx = { width: 112, height: 36, objectFit: 'cover' as const, opacity: 0.95, display: 'block' };

const sectionLabelSx = {
  px: 2.5,
  py: 0.5,
  fontSize: '0.65rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.3)',
  letterSpacing: '0.1em',
  textTransform: 'uppercase' as const,
  mb: 0.5,
};

export type Membership = {
  id: number;
  name?: string;
  lastname?: string;
  is_admin?: boolean;
  is_patron?: boolean;
  role?: string;
  member?: { username?: string; email?: string };
  /** The name they sign in with at this business's TRUE POS (older servers don't send it). */
  login?: string;
};

type ShellContextValue = {
  membership: Membership | null; organizationName: string;
  /** The business's kind and modules (null until loaded: everything shows). */
  features?: Features | null; setFeatures?: (features: Features) => void;
};
export const ShellContext = React.createContext<ShellContextValue>({ membership: null, organizationName: '' });

const initials = (text: string) =>
  text.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';

const AppShell = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { pathname, search, hash } = useLocation();
  const { slug: urlSlug } = useParams();
  const navigate = useNavigate();
  const axios = useAxios();
  const { logoutUser, user } = React.useContext(AuthContext);

  const [desktopOpen, setDesktopOpen] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [subscriptionDialog, setSubscriptionDialog] = React.useState(false);
  const [membership, setMembership] = React.useState<Membership | null>(null);
  const [organizationName, setOrganizationName] = React.useState('');
  const [slugKnown, setSlugKnown] = React.useState(businessSlug());
  const [features, setFeatures] = React.useState<Features | null>(null);

  // Every authenticated page needs an organization; send owners without one to onboarding.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('management/my-membership/');
        if (cancelled) return;
        setMembership(data);
        const org = await axios.get('management/organization/');
        if (cancelled) return;
        setOrganizationName(org.data?.name ?? '');
        // The organization says which business this is (tokens from older sessions don't carry it).
        if (org.data?.slug) {
          setBusinessSlug(org.data.slug);
          setSlugKnown(org.data.slug);
        }
        // Which modules it uses (an older server without it: everything shows).
        axios.get('management/organization/features/').then(r => { if (!cancelled) setFeatures(r.data); }).catch(() => {});
      } catch (error: any) {
        if (!cancelled && error?.response?.status === 403) {
          navigate('/create-organization', { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the mobile drawer after navigating.
  React.useEffect(() => { setMobileOpen(false); }, [pathname]);

  const sidebarOpen = isDesktop ? desktopOpen : mobileOpen;
  const toggleSidebar = () => (isDesktop ? setDesktopOpen(o => !o) : setMobileOpen(o => !o));

  const handleSubscription = async () => {
    setAnchorEl(null);
    try {
      const response = await axios.post('management/create-portal-session/');
      if (response.data.url) {
        window.location.href = response.data.url;
        return;
      }
    } catch (error) {
      // Fall through to the "no subscription" dialog.
    }
    setSubscriptionDialog(true);
  };

  const displayName = [membership?.name, membership?.lastname].filter(Boolean).join(' ')
    || membership?.login || membership?.member?.username || user?.username || '';
  const email = membership?.member?.email || user?.email || '';

  const sidebar = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {!isDesktop && (
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, minHeight: APPBAR_HEIGHT }}>
          <Box component="img" src={TRUE_LOGO_WHITE} alt="TRUE" sx={{ ...logoSx, pointerEvents: 'none' }} />
        </Box>
      )}
      {NAV_SECTIONS.map((section, i) => (
        <React.Fragment key={section.label}>
          {i > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2, my: 1 }} />}
          <List sx={{ px: 1, pt: i === 0 ? 2 : 0 }}>
            <Typography sx={sectionLabelSx}>{section.label}</Typography>
            {section.items.filter(item => (!item.modules || item.modules.some(key => moduleOn(features, key)))
              && (!item.admin || !membership || membership.is_admin)).map(item => (
              <ListItem key={item.to} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.to}
                  selected={isActive(item.to, pathname)}
                  sx={navItemSx}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </React.Fragment>
      ))}
      <Box sx={{ flexGrow: 1 }} />
      {organizationName && (
        <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Typography sx={{ ...sectionLabelSx, px: 0, mb: 0.25 }}>Organización</Typography>
          <Typography noWrap sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, fontSize: '0.875rem' }}>
            {organizationName}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const drawerPaperSx = {
    width: SIDEBAR_WIDTH,
    boxSizing: 'border-box' as const,
    bgcolor: TRUE_COLORS.primary,
    boxShadow: '4px 0 24px rgba(0,0,0,0.18)',
    ...(isDesktop && { top: APPBAR_HEIGHT, height: `calc(100% - ${APPBAR_HEIGHT}px)` }),
  };

  // The dashboard's address is the business's own: /admin/… (old links) and /<another slug>/admin/… move to
  // /<this slug>/admin/… (businessHistory adds the slug to the path without it).
  if (slugKnown && urlSlug !== slugKnown) {
    // Written with the slug, not left to businessHistory: whatever happened to it, this lands on the business's page.
    return <Navigate to={`/${slugKnown}${withoutBusiness(pathname)}${search}${hash}`} replace />;
  }

  return (
    <ShellContext.Provider value={{ membership, organizationName, features, setFeatures }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: t => t.zIndex.drawer + 1,
          backgroundImage: TRUE_COLORS.headerGradient,
          boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Toolbar sx={{ minHeight: `${APPBAR_HEIGHT}px !important`, gap: 1 }}>
          <IconButton
            edge="start"
            aria-label="Mostrar u ocultar menú"
            onClick={toggleSidebar}
            sx={{ mr: 1, color: 'rgba(255,255,255,0.85)' }}
          >
            <MenuIcon />
          </IconButton>
          <Box component={Link} to="/admin" sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="img" src={TRUE_LOGO_WHITE} alt="TRUE" sx={logoSx} />
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            onClick={e => setAnchorEl(e.currentTarget)}
            aria-controls="account-menu"
            aria-haspopup="true"
            endIcon={<KeyboardArrowDownIcon sx={{ display: { xs: 'none', sm: 'block' } }} />}
            sx={{ color: 'rgba(255,255,255,0.9)', px: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
          >
            <Avatar sx={{ width: 32, height: 32, mr: { xs: 0, sm: 1.25 }, fontSize: '0.8rem', fontWeight: 700, bgcolor: TRUE_COLORS.secondary }}>
              {initials(displayName)}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left', lineHeight: 1.2 }}>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#fff', maxWidth: 200 }} noWrap>
                {displayName}
              </Typography>
              {organizationName && (
                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)', maxWidth: 200 }} noWrap>
                  {organizationName}
                </Typography>
              )}
            </Box>
          </Button>
        </Toolbar>
      </AppBar>

      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 220 } } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }} noWrap>{displayName}</Typography>
          {email && email !== displayName && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>{email}</Typography>
          )}
        </Box>
        <Divider />
        <MenuItem onClick={() => { setAnchorEl(null); navigate('/admin/profile'); }} sx={{ fontSize: '0.875rem', py: 1.25 }}>
          Perfil
        </MenuItem>
        <MenuItem onClick={handleSubscription} sx={{ fontSize: '0.875rem', py: 1.25, gap: 1 }}>
          Suscripción <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary', ml: 'auto' }} />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => { setAnchorEl(null); logoutUser(); }}
          sx={{ fontSize: '0.875rem', color: TRUE_COLORS.danger, py: 1.25 }}
        >
          Cerrar sesión
        </MenuItem>
      </Menu>

      <Drawer
        variant={isDesktop ? 'persistent' : 'temporary'}
        open={sidebarOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': drawerPaperSx }}
      >
        {sidebar}
      </Drawer>

      <Box
        component="main"
        sx={{
          ml: isDesktop && desktopOpen ? `${SIDEBAR_WIDTH}px` : 0,
          pt: `${APPBAR_HEIGHT}px`,
          minHeight: '100vh',
          bgcolor: 'background.default',
          transition: theme.transitions.create('margin-left', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Outlet />
      </Box>

      <Dialog open={subscriptionDialog} onClose={() => setSubscriptionDialog(false)}>
        <DialogTitle>No tienes una suscripción activa</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Escríbenos y te ayudamos a elegir el plan para tus sucursales.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSubscriptionDialog(false)}>Cerrar</Button>
          <Button variant="contained" href="mailto:luis@truedata.com.mx,dipepere@truedata.com.mx?subject=Plan TRUE POS">
            Contactar
          </Button>
        </DialogActions>
      </Dialog>
    </ShellContext.Provider>
  );
};

export default AppShell;
